import React, { useEffect, useState } from 'react';
import styles from './technical-competencies.module.scss';
import { tooltip_grey_icon } from '../../assets/img';
import { Spin, Tooltip } from 'antd';
import { BasicTable } from '../../assets/common';
import { useDispatch, useSelector } from 'react-redux';
import talentProfileApi from '../../services/talentProfiles';
import { TP_PRINT_SECTION_NAME } from '../TalentProfilePrintPreview/constants';
import { updatePrintData } from '../../pages/TalentProfilePage/store/printProfileSlice';
import { updateDownloadData } from '../../pages/TalentProfilePage/store/downloadProfileSlice';

const INITIAL_COMPETENCIES = {
  positions: [],
  pcp: [],
};

const CompetencyTable = ({ data, isJCP }) => {
  const columnsJCP = [
    {
      title: 'Competency',
      dataIndex: 'competency',
      key: 'competency',
      width: isJCP ? '39%' : '80%',
      render: (text, record) => {
        return (
          <>
            {record.no ? (
              <div className={styles.wrapper_competency}>
                <span className={`${styles.number}`}>{record.no}</span>
                <span>{text}</span>
              </div>
            ) : (
              <div className={styles.wrapper_skg}>
                <div className={styles.skg}>
                  <span />
                  {text}
                </div>
              </div>
            )}
          </>
        );
      },
    },
    {
      title: 'Job Requirements',
      dataIndex: 'job_requirements',
      key: 'job_requirements',
      width: '19%',
      align: 'center',
      render: (text) => {
        return <>{text != null ? text : ''}</>;
      },
    },
    {
      title: 'Proficiency Level',
      dataIndex: 'proficiency_level',
      key: 'proficiency_level',
      width: isJCP ? '18%' : '20%',
      align: 'center',
      render: (text) => {
        return <>{text != null ? text : ''}</>;
      },
    },
    {
      title: 'Sub-Total Percentage',
      dataIndex: 'sub_total_percentage',
      key: 'sub_total_percentage',
      width: '24%',
      align: 'center',
      render: (text, record) => {
        return (
          <div className={styles.wrapper_sub_total_percentage}>
            <span>{text != null ? `${text} %` : ''}</span>
            <span className={`${styles.number} ${getBgColorSubTotalPercent(record)}`} />
          </div>
        );
      },
    },
  ];

  const columnsPCP = columnsJCP.filter((column) => ['Competency', 'Proficiency Level'].includes(column.title));

  const getBgColorSubTotalPercent = (record) => {
    if (!record.no) return;
    if (!record.proficiency_level && !record.is_not_red) {
      return styles.background_red;
    }

    if (!record.job_requirements) {
      return styles.background_grey;
    }

    if (record.job_requirements > record.proficiency_level) {
      return styles.background_yellow;
    }

    if (record.job_requirements < record.proficiency_level) {
      return styles.background_green;
    }

    if (record.job_requirements === record.proficiency_level) {
      return styles.background_green;
    }

    return styles.background_transparent;
  };

  const roundIfDecimal = (value) => {
    return Number.isInteger(value) ? value : parseFloat(value.toFixed(2));
  };

  const getTotalPercent = (data) => {
    const dataNotNull = data?.filter((d) => d.sub_total_percentage != null);
    const totalPercent = dataNotNull.length > 0 ? dataNotNull?.reduce((sum, row) => sum + row.sub_total_percentage, 0) : null;
    return totalPercent !== null ? `${roundIfDecimal(totalPercent / dataNotNull.length)} %` : '';
  };

  return (
    <BasicTable
      className={styles.wrapper_table}
      rowKey={'id'}
      dataSource={data}
      columns={isJCP ? columnsJCP : columnsPCP}
      pagination={false}
      styles={{ headerColor: '#575757', headerBackground: '#E5ECF5', headerSize: '12', marginTop: '0px' }}
      getPopupContainer={(trigger) => trigger.parentElement}
      footer={
        isJCP
          ? () => (
              <div className={styles.wrapper_footer}>
                <span className={styles.total_percent}>Total Percentage:</span>
                <span className={styles.total_percent}>{getTotalPercent(data)}</span>
              </div>
            )
          : null
      }
      rowClassName={(record) => (record.no % 2 ? styles.even_row : styles.odd_row)}
    />
  );
};

const TechnicalCompetencies = ({ profileId, tokenTalentProfile, isPrintTP, fetchedData }) => {
  const [competencies, setCompetencies] = useState(INITIAL_COMPETENCIES);
  const user = useSelector((state) => state.user);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();

  const fetchApi = async () => {
    if (!user.roleActive || !profileId) return;

    try {
      setLoading(true);
      const response = await talentProfileApi.fetchTechnicalCompetencies({
        profileId,
        roleUser: user,
        tokenTalentProfile,
      });

      if (response.status === 200) {
        const result = response?.data?.result;
        setCompetencies(mappingData(result));
        dispatch(
          updatePrintData({
            sectionName: TP_PRINT_SECTION_NAME.TECHNICAL_COMPETENCY,
            data: result,
          })
        );
        dispatch(
          updateDownloadData({
            sectionName: TP_PRINT_SECTION_NAME.TECHNICAL_COMPETENCY,
            data: result,
          })
        );
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isPrintTP && fetchedData) {
      setCompetencies(mappingData(fetchedData));
      return;
    }

    fetchApi();
  }, [user.roleActive.roleId, profileId, fetchedData, isPrintTP]);

  return (
    <Spin spinning={loading} size="small">
      <div className={styles.section_plan} data-print-section={TP_PRINT_SECTION_NAME.JOB_COMPETENCY_PROFILE}>
        <div className={styles.section_name}>
          <span>Job Competency Profile (JCP)</span>
          {!isPrintTP && (
            <Tooltip
              overlayInnerStyle={{
                fontSize: '12px',
                fontWeight: 600,
                fontFamily: `'Inter', sans-serif`,
                borderRadius: '4px',
                background: '#000',
                color: '#fff',
              }}
              placement="bottom"
              title={'JCP Technical Competencies is based on current job competencies profile from myCareerX'}
            >
              <img src={tooltip_grey_icon} alt="" />
            </Tooltip>
          )}
        </div>
        <div>
          {competencies.positions?.map((position, index) => (
            <div key={index}>
              <div className={styles.position}>Position: {position?.position_name}</div>
              <div className={styles.wrapper} key={index}>
                <CompetencyTable data={position?.competencies || []} isJCP={true} />
              </div>
            </div>
          ))}
          {!competencies.positions.length && <CompetencyTable data={[]} isJCP={true} />}
        </div>
      </div>

      <div className={styles.section_plan} data-print-section={TP_PRINT_SECTION_NAME.PERSONAL_COMPETENCY_PROFILE}>
        <div className={styles.section_name_pcp}>
          <span>Personal Competency Profile (PCP)</span>
          {!isPrintTP && (
            <Tooltip
              overlayInnerStyle={{
                fontSize: '12px',
                fontWeight: 600,
                fontFamily: `'Inter', sans-serif`,
                borderRadius: '4px',
                background: '#000',
                color: '#fff',
                marginLeft: '5px',
              }}
              placement="bottom"
              title={
                'PCP Technical Competencies is based on accumulated competencies not registered to the current role. Data is based on what is available in myCareerX only.'
              }
            >
              <img src={tooltip_grey_icon} alt="" />
            </Tooltip>
          )}
        </div>

        <div className={styles.wrapper}>
          <CompetencyTable data={competencies?.pcp || []} isJCP={false} />
        </div>
      </div>
    </Spin>
  );
};

export default TechnicalCompetencies;

const mappingData = (data) => {
  if (!data) return INITIAL_COMPETENCIES;
  return {
    pcp: data.pcp.map((item, index) => ({ ...item, no: index + 1 })),
    positions: data.positions.map((p) => {
      let skgCompetencies = [];
      p.skgs.forEach((skg) => {
        skgCompetencies = [
          ...skgCompetencies,
          {
            no: 0,
            competency: `SKG: ${skg.skg}. ${skg.skg_descr}`,
            job_requirements: null,
            proficiency_level: null,
            sub_total_percentage: null,
            assign_status: null,
            is_not_red: null,
          },
          ...skg.competencies.map((com, i) => ({
            ...com,
            no: i + 1,
          })),
        ];
      });
      return {
        position_code: p.position_code,
        position_name: p.position_name,
        competencies: skgCompetencies,
      };
    }),
  };
};
